import React from 'react';
import { Box, Button, Typography, Select, MenuItem } from '@mui/material';
import { pageRange } from './pageRange';

const fontBase = { fontFamily: 'Roboto, Arial, sans-serif' };

export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const navButtonSx = {
  textTransform: 'none',
  color: '#374151',
  fontSize: '13px',
  minWidth: 'auto',
  px: 1.25,
  '&:disabled': { color: '#9ca3af' },
};

/**
 * The footer under every list table.
 *
 * The pages each had their own Previous/Next pair and no way to tell how far
 * through a list you were, how many rows there were in total, or to ask for
 * more than ten at a time. One of them ("disabled={rows.length < rowsPerPage}")
 * could not tell a last full page from a page with more behind it, so the last
 * ten rows of an exactly-divisible list were unreachable.
 *
 * `page` is 1-based.
 */
const DataTablePagination = ({
  page,
  total,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS,
  label,
  note,
  loading = false,
}) => {
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const current = Math.min(Math.max(1, page), pageCount);
  const firstRow = total === 0 ? 0 : (current - 1) * rowsPerPage + 1;
  const lastRow = Math.min(current * rowsPerPage, total);

  const go = (next) => {
    if (next < 1 || next > pageCount || next === current) return;
    onPageChange(next);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        p: 2,
        borderTop: '1px solid #e0e0e0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ ...fontBase, color: '#6b7280', fontSize: '13px' }}>
          {label || (total === 0
            ? 'No rows'
            : `Showing ${firstRow}–${lastRow} of ${total}`)}
          {note ? ` · ${note}` : ''}
        </Typography>

        {onRowsPerPageChange && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ ...fontBase, color: '#6b7280', fontSize: '13px' }}>
              Rows per page
            </Typography>
            <Select
              size="small"
              value={rowsPerPage}
              onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
              sx={{
                ...fontBase,
                fontSize: '13px',
                height: '32px',
                color: '#374151',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <MenuItem key={option} value={option} sx={{ ...fontBase, fontSize: '13px' }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Button size="small" onClick={() => go(1)} disabled={loading || current === 1} sx={navButtonSx}>
          First
        </Button>
        <Button size="small" onClick={() => go(current - 1)} disabled={loading || current === 1} sx={navButtonSx}>
          Previous
        </Button>

        {pageRange(current, pageCount).map((entry, i) =>
          entry === '…' ? (
            <Typography
              key={`gap-${i}`}
              sx={{ ...fontBase, color: '#9ca3af', fontSize: '13px', px: 0.5, userSelect: 'none' }}
            >
              …
            </Typography>
          ) : (
            <Button
              key={entry}
              size="small"
              onClick={() => go(entry)}
              disabled={loading}
              aria-current={entry === current ? 'page' : undefined}
              sx={{
                ...navButtonSx,
                minWidth: '32px',
                fontWeight: entry === current ? 600 : 400,
                color: entry === current ? '#fff' : '#374151',
                backgroundColor: entry === current ? '#578A18' : 'transparent',
                '&:hover': {
                  backgroundColor: entry === current ? '#467014' : '#f3f4f6',
                },
              }}
            >
              {entry}
            </Button>
          )
        )}

        <Button
          size="small"
          onClick={() => go(current + 1)}
          disabled={loading || current >= pageCount}
          sx={navButtonSx}
        >
          Next
        </Button>
        <Button
          size="small"
          onClick={() => go(pageCount)}
          disabled={loading || current >= pageCount}
          sx={navButtonSx}
        >
          Last
        </Button>
      </Box>
    </Box>
  );
};

export default DataTablePagination;
